/**
 * Slice 20A — Runtime wardrobe-mount loader.
 *
 * Loads a wardrobe-piece GLB extracted by `blender_extract_wardrobe.py`
 * (Slice 19A) and mounts it onto a host avatar by re-binding the piece's
 * `SkinnedMesh.skeleton.bones[]` array to the host avatar's matching-name
 * bones. Because every F4 avatar shares one bind pose / bone naming
 * convention (and likewise for M4), a piece extracted from any donor
 * deforms correctly when worn by any host of the same family.
 *
 * This is a vanilla Three.js module (no React, no react-three-fiber)
 * so it can be called from inside `useFrame` hooks, from imperative
 * SDK consumers, or from the Wardrobe BETA panel directly.
 *
 * Wire model:
 *
 *   host scene graph (loaded GLB):
 *     Scene
 *      └ Armature_F4 (THREE.Bone tree)
 *      └ SK_F4_Head      (always-on, never touched)
 *      └ SK_F4_Hands     (always-on, never touched)
 *      └ SK_F4_Torso_Underwear  (always-on, hidden when piece in 'top' slot equipped)
 *      └ SK_F4_Torso_Naomi      (donor's signature top — hidden when overridden)
 *      └ SK_F4_Legs_Underwear   (always-on, hidden when piece in 'pants' slot equipped)
 *      └ SK_F4_Legs_Naomi       (donor's signature pants — hidden when overridden)
 *      └ SK_F4_Hair_Bun_Yuki    (donor's signature hair — hidden when overridden)
 *      └ SK_F4_Feet_Boots_Naomi (donor's signature shoes — hidden when overridden)
 *
 *   wardrobe piece GLB (extracted, e.g. top-kiera-kiera.glb):
 *     Scene
 *      └ Armature_F4 (a copy of the master armature — discarded on mount)
 *      └ SK_F4_Torso_Kiera (the actual mesh — re-skinned to host's armature)
 *
 * On `mountWardrobePiece(host, piece)`:
 *
 * 1. Load the piece's GLB.
 * 2. Find the SkinnedMesh inside it.
 * 3. Build a name→bone map from the host's armature.
 * 4. Re-point `mesh.skeleton.bones[]` so each bone is the host's bone
 *    of the same name (NOT a copy — the SAME object).
 * 5. Hide every host mesh whose slot matches the new piece (e.g. mounting
 *    a 'top' hides the donor's `SK_F4_Torso_<style>` and the underwear
 *    `SK_F4_Torso_Underwear`).
 * 6. Add the piece mesh as a child of the host's scene root so it inherits
 *    the host's world transform.
 *
 * On `unmountWardrobePiece(host, slot)`:
 * - Remove the previously-mounted mesh.
 * - Re-show the donor's default piece for that slot (and the underwear
 *   if applicable).
 *
 * Family compatibility (F4 vs M4) is enforced by `WardrobeManifestPiece.compatibleWith`
 * — this module does NOT validate family at the bone level; if you mount an
 * F4 piece on an M4 host, the bone-name lookup will silently fail because
 * the bone names don't overlap. The Wardrobe panel must filter by family
 * before exposing pieces to the user.
 */

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { patchHairMaterial } from './hairMaterialPatch';

// =============================================================================
// TYPES — wire-compatible with `wardrobe/manifest.json` produced by Slice 19B
// =============================================================================

export type WardrobeSlot = 'top' | 'pants' | 'shoes' | 'hair' | 'dress';

export interface WardrobeManifestPiece {
  id: string;
  slot: WardrobeSlot;
  family: 'f4' | 'm4';
  donor: string;
  style: string;
  displayName: string;
  meshName: string;
  /** URL relative to the avatar-library root, e.g. "wardrobe/top-naomi-naomi.glb" */
  file: string;
  sizeKb: number;
  defaultFor: string[];
  compatibleWith: string[];
}

export interface WardrobeManifest {
  version: number;
  generatedAt: string;
  pieces: WardrobeManifestPiece[];
  slots: Record<WardrobeSlot, { displayName: string; alwaysOn: boolean; mutuallyExclusiveWith?: WardrobeSlot[] }>;
  families: Record<'f4' | 'm4', { hosts: string[] }>;
}

// =============================================================================
// MOUNTED-PIECE BOOKKEEPING
// =============================================================================

/**
 * One mounted piece, plus enough metadata to undo the mount.
 *
 * Cached on the host scene root via `userData.__wardrobeEquipped` so the
 * Wardrobe panel can introspect "what's equipped" without walking the
 * scene graph.
 */
interface EquippedPiece {
  pieceId: string;
  slot: WardrobeSlot;
  /** Mesh added to the scene (subtree, in case piece has multiple meshes) */
  mountedMeshes: THREE.SkinnedMesh[];
  /** Host meshes that were hidden during mount; restored on unmount */
  hiddenHostMeshes: THREE.SkinnedMesh[];
  /** Disposers for textures/geometries owned by the piece */
  disposers: Array<() => void>;
}

interface WardrobeState {
  equipped: Map<WardrobeSlot, EquippedPiece>;
}

const WARDROBE_STATE_KEY = '__wardrobeState';

function getOrCreateState(host: THREE.Object3D): WardrobeState {
  const ud = (host.userData ??= {});
  if (!ud[WARDROBE_STATE_KEY]) {
    ud[WARDROBE_STATE_KEY] = { equipped: new Map() } satisfies WardrobeState;
  }
  return ud[WARDROBE_STATE_KEY] as WardrobeState;
}

// =============================================================================
// LOADER (singleton — reuses Three's GLTFLoader + DRACOLoader)
// =============================================================================

let cachedLoader: GLTFLoader | null = null;

function getLoader(dracoDecoderPath: string = 'https://www.gstatic.com/draco/v1/decoders/'): GLTFLoader {
  if (cachedLoader) return cachedLoader;
  const loader = new GLTFLoader();
  const draco = new DRACOLoader();
  draco.setDecoderPath(dracoDecoderPath);
  loader.setDRACOLoader(draco);
  cachedLoader = loader;
  return loader;
}

async function loadGltf(url: string): Promise<THREE.Group> {
  const loader = getLoader();
  return await new Promise<THREE.Group>((resolve, reject) => {
    loader.load(
      url,
      (gltf) => resolve(gltf.scene),
      undefined,
      (err) => reject(err),
    );
  });
}

// =============================================================================
// HOST-INTROSPECTION HELPERS
// =============================================================================

function buildHostBoneMap(host: THREE.Object3D): Map<string, THREE.Bone> {
  const map = new Map<string, THREE.Bone>();
  host.traverse((obj) => {
    if ((obj as THREE.Bone).isBone) {
      const name = obj.name;
      if (!map.has(name)) {
        map.set(name, obj as THREE.Bone);
      }
    }
  });
  return map;
}

/**
 * Find the first SkinnedMesh on the host scene whose skeleton has a
 * bones-by-name overlap with the piece's expected bones. Used to read
 * the host's AUTHORED bone-inverse matrices (i.e. the host's bind pose)
 * so the piece can be re-bound to the host's rest pose instead of the
 * donor's. Without this retargeting, equipping a piece extracted from
 * one host (e.g. Riley's dress, baked against SK_F4M4) on a host with
 * a subtly-different rest pose (e.g. Cara — Xandra's armature shares
 * 341/342 bone names but the bind pose diverges enough to explode
 * skinned vertices into spikes) produces visible mesh corruption.
 */
function findPrimaryHostSkinnedMesh(host: THREE.Object3D): THREE.SkinnedMesh | null {
  let primary: THREE.SkinnedMesh | null = null;
  let primaryBoneCount = 0;
  host.traverse((obj) => {
    if (!(obj as THREE.SkinnedMesh).isSkinnedMesh) return;
    const mesh = obj as THREE.SkinnedMesh;
    const boneCount = mesh.skeleton?.bones?.length ?? 0;
    // Prefer the mesh with the most bones — that's almost certainly the
    // body mesh skinned to the full armature, not a hair or eyelash mesh
    // skinned to a partial bone subset.
    if (boneCount > primaryBoneCount) {
      primary = mesh;
      primaryBoneCount = boneCount;
    }
  });
  return primary;
}

/**
 * Build a name → boneInverse map from the host's skeleton. These are the
 * inverse bind matrices the host was AUTHORED with — i.e. they encode the
 * host's exact rest pose. When we mount a foreign piece, we look up each
 * piece-bone by name and use the host's authored inverse for that bone
 * so the piece deforms relative to the host's rest pose (not the donor's).
 */
function buildHostBoneInverseMap(host: THREE.Object3D): Map<string, THREE.Matrix4> {
  const map = new Map<string, THREE.Matrix4>();
  const hostMesh = findPrimaryHostSkinnedMesh(host);
  if (!hostMesh?.skeleton) return map;
  const { bones, boneInverses } = hostMesh.skeleton;
  for (let i = 0; i < bones.length; i++) {
    const bone = bones[i];
    const inverse = boneInverses[i];
    if (bone && inverse && !map.has(bone.name)) {
      map.set(bone.name, inverse);
    }
  }
  return map;
}

function findHostMeshesForSlot(
  host: THREE.Object3D,
  slot: WardrobeSlot,
  family: 'f4' | 'm4',
): THREE.SkinnedMesh[] {
  // Map slot → mesh-name regex(es). These mirror the slot patterns in the
  // Blender extractor (see `blender_extract_wardrobe.py → SLOT_PATTERNS`)
  // but include the underwear meshes as well so we hide them when a top
  // or pants is equipped.
  //
  // The "legacy" patterns (F4_Torso, UE_Bikini_Top, "Hair *", *_Bangs)
  // are Cara (`f4-xandra.glb`) — she predates the `SK_F4_<Slot>_<Style>`
  // naming scheme used by the modern F4 avatars. Kept as legitimate
  // matches so wardrobe pieces extracted from modern F4 avatars hide
  // Cara's defaults correctly when equipped on her.
  const upper = family.toUpperCase();
  const isF4 = family === 'f4';
  const patterns: Record<WardrobeSlot, RegExp[]> = {
    top: [
      new RegExp(`^SK_${upper}_Torso_`),
      ...(isF4 ? [/^F4_Torso$/, /^UE_Bikini_Top$/] : []),
    ],
    pants: [
      new RegExp(`^SK_${upper}_Legs_`),
      ...(isF4 ? [/^F4_Legs_/] : []),
    ],
    shoes: [
      new RegExp(`^SK_${upper}_Feet_`),
      ...(isF4 ? [/^F4_Feet$/] : []),
    ],
    hair: [
      new RegExp(`^SK_${upper}_Hair_`),
      // Cara's Yuki_Bangs + Hair 2_Back legacy
      ...(isF4 ? [/^Hair /, /_Bangs$/, /^Yuki_/] : []),
    ],
    dress: [
      new RegExp(`^SK_${upper}_Dress_`),
      // Dress also hides Torso_* and Legs_* so the upper body / legs aren't double-clothed
      new RegExp(`^SK_${upper}_Torso_`),
      new RegExp(`^SK_${upper}_Legs_`),
      ...(isF4 ? [/^F4_Torso$/, /^UE_Bikini_Top$/, /^F4_Legs_/] : []),
    ],
  };

  const matches: THREE.SkinnedMesh[] = [];
  const slotPatterns = patterns[slot];
  host.traverse((obj) => {
    if (!(obj as THREE.SkinnedMesh).isSkinnedMesh) return;
    const cleanName = obj.name.replace(/\.\d+$/, '');
    if (slotPatterns.some((re) => re.test(cleanName))) {
      matches.push(obj as THREE.SkinnedMesh);
    }
  });
  return matches;
}

// =============================================================================
// PUBLIC API
// =============================================================================

export interface MountWardrobePieceOptions {
  /** Host avatar scene root (e.g. the gltf.scene returned by useGLTF). */
  host: THREE.Object3D;
  /** Piece manifest entry (or just enough fields — at minimum `slot`, `file`, `id`). */
  piece: Pick<WardrobeManifestPiece, 'id' | 'slot' | 'family' | 'meshName' | 'file'>;
  /** Base URL for the wardrobe library (e.g. "https://models.camlinconnect.com.au/"). */
  baseUrl: string;
  /** Optional Draco decoder path (defaults to Google CDN). */
  dracoDecoderPath?: string;
}

/**
 * Load the piece GLB and mount it on the host avatar.
 *
 * Idempotency: if a piece is already equipped in the same slot, it is
 * unmounted first.
 */
export async function mountWardrobePiece(
  opts: MountWardrobePieceOptions,
): Promise<EquippedPiece> {
  const { host, piece, baseUrl } = opts;
  const state = getOrCreateState(host);

  // Unmount whatever's already in this slot
  if (state.equipped.has(piece.slot)) {
    unmountWardrobePiece(host, piece.slot);
  }

  // Resolve URL: baseUrl + relative file (avoid double-slash)
  const url = baseUrl.replace(/\/+$/, '') + '/' + piece.file.replace(/^\/+/, '');
  const pieceScene = await loadGltf(url);

  // Find the piece's skinned mesh(es) — typically one, but allow several
  const pieceMeshes: THREE.SkinnedMesh[] = [];
  pieceScene.traverse((obj) => {
    if ((obj as THREE.SkinnedMesh).isSkinnedMesh) {
      pieceMeshes.push(obj as THREE.SkinnedMesh);
    }
  });
  if (!pieceMeshes.length) {
    throw new Error(`[wardrobe] piece ${piece.id} contains no SkinnedMesh`);
  }

  // Re-bind every piece-mesh's skeleton to the host's bones (by name).
  //
  // BIND-POSE RETARGET (May 17 2026 — Cara wardrobe followup): we ALSO
  // swap the piece's `boneInverses` for the host's AUTHORED inverses
  // (looked up by bone name). The piece's `boneInverses` encode the
  // DONOR's rest pose. If the host's rest pose differs even slightly
  // (Cara vs the modern F4 line is the canonical case — 341/342 bone
  // names match but rest-pose positions diverge enough to explode
  // skinned vertices into spikes), keeping the donor's inverses would
  // skin every vertex relative to the donor's rest, then position
  // them via the host's bones, producing the spike geometry. Using
  // the host's authored inverses makes the piece deform relative to
  // the HOST's rest, which is what we want. For modern F4 hosts where
  // the rest pose matches the donor's exactly, the host inverses ARE
  // the donor inverses, so this is a no-op.
  const hostBones = buildHostBoneMap(host);
  const hostBoneInverses = buildHostBoneInverseMap(host);
  for (const mesh of pieceMeshes) {
    const oldBones = mesh.skeleton.bones;
    const oldInverses = mesh.skeleton.boneInverses;
    const newBones: THREE.Bone[] = [];
    const newBoneInverses: THREE.Matrix4[] = [];
    let unmappedCount = 0;
    let retargetedCount = 0;
    for (let i = 0; i < oldBones.length; i++) {
      const bone = oldBones[i];
      const hostBone = hostBones.get(bone.name);
      if (hostBone) {
        newBones.push(hostBone);
      } else {
        // No matching host bone — keep the piece's original bone so the
        // skeleton array remains the right length. Skinning weights to
        // a bone the host can't animate will simply hold the bind pose
        // for those vertices, which is the safest fallback.
        newBones.push(bone);
        unmappedCount++;
      }
      const hostInverse = hostBoneInverses.get(bone.name);
      if (hostInverse) {
        newBoneInverses.push(hostInverse.clone());
        retargetedCount++;
      } else {
        // No host inverse for this bone (host doesn't have it) — keep
        // the piece's original inverse. Won't matter because the bone
        // itself is unmapped too.
        newBoneInverses.push(oldInverses[i]);
      }
    }
    if (unmappedCount > 0) {
      console.warn(
        `[wardrobe] ${piece.id}: ${unmappedCount}/${oldBones.length} bones did not match host (` +
          `family mismatch? expected ${piece.family}, host bones: ${hostBones.size})`,
      );
    }
    if (retargetedCount > 0 && retargetedCount < oldBones.length) {
      console.debug(
        `[wardrobe] ${piece.id}: retargeted ${retargetedCount}/${oldBones.length} bone inverses to host rest pose`,
      );
    }

    // Build a fresh skeleton with the host bones + host-authored bind matrices
    const newSkeleton = new THREE.Skeleton(newBones, newBoneInverses);
    mesh.skeleton = newSkeleton;
    mesh.bind(newSkeleton, mesh.bindMatrix);
  }

  // Hide host meshes for this slot
  const hostMeshesToHide = findHostMeshesForSlot(host, piece.slot, piece.family);
  for (const m of hostMeshesToHide) {
    if (m.userData.__wardrobeWasVisible === undefined) {
      m.userData.__wardrobeWasVisible = m.visible;
    }
    m.visible = false;
  }

  // Add the piece's meshes to the host scene root
  for (const m of pieceMeshes) {
    host.add(m);
  }

  // Slice 22 — Patch any hair material on the just-mounted piece. F4
  // wardrobe-hair pieces extracted from donor avatars carry the same
  // alphaMode=BLEND + baseColorFactor[3] > 1 quirk as the base avatars
  // (since they came from the same donor GLB). The base avatar's
  // mount-time patch in Avatar.tsx doesn't see them — they enter the
  // scene graph after that traverse — so re-apply per-mesh here.
  for (const m of pieceMeshes) {
    patchHairMaterial(m);
  }

  const equipped: EquippedPiece = {
    pieceId: piece.id,
    slot: piece.slot,
    mountedMeshes: pieceMeshes,
    hiddenHostMeshes: hostMeshesToHide,
    disposers: pieceMeshes.flatMap((m) => [
      () => m.geometry?.dispose?.(),
      () => {
        const mat = m.material as THREE.Material | THREE.Material[] | undefined;
        if (Array.isArray(mat)) mat.forEach((x) => x?.dispose?.());
        else mat?.dispose?.();
      },
    ]),
  };
  state.equipped.set(piece.slot, equipped);

  // If we just equipped a dress, also drop any 'top' / 'pants' from state
  // (their meshes were already hidden by the dress's slot logic, but the
  // state map should reflect that they're no longer equipped).
  if (piece.slot === 'dress') {
    for (const exclusive of ['top', 'pants'] as WardrobeSlot[]) {
      const prev = state.equipped.get(exclusive);
      if (prev) {
        // Don't restore host meshes — the dress already hides them
        for (const m of prev.mountedMeshes) host.remove(m);
        prev.disposers.forEach((d) => d());
        state.equipped.delete(exclusive);
      }
    }
  }
  // Conversely: equipping a top or pants implicitly removes a dress
  if ((piece.slot === 'top' || piece.slot === 'pants') && state.equipped.has('dress')) {
    unmountWardrobePiece(host, 'dress');
  }

  return equipped;
}

/**
 * Remove the piece equipped in the given slot. No-op if nothing's equipped.
 */
export function unmountWardrobePiece(host: THREE.Object3D, slot: WardrobeSlot): void {
  const state = getOrCreateState(host);
  const equipped = state.equipped.get(slot);
  if (!equipped) return;

  for (const m of equipped.mountedMeshes) {
    host.remove(m);
  }
  for (const m of equipped.hiddenHostMeshes) {
    if (m.userData.__wardrobeWasVisible !== undefined) {
      m.visible = m.userData.__wardrobeWasVisible;
      delete m.userData.__wardrobeWasVisible;
    } else {
      m.visible = true;
    }
  }
  equipped.disposers.forEach((d) => {
    try { d(); } catch { /* swallow disposal errors */ }
  });
  state.equipped.delete(slot);
}

/**
 * Reset every wardrobe slot to the host avatar's default look.
 */
export function resetWardrobeToDefault(host: THREE.Object3D): void {
  const state = getOrCreateState(host);
  const slots: WardrobeSlot[] = [];
  state.equipped.forEach((_, slot) => slots.push(slot));
  for (const slot of slots) {
    unmountWardrobePiece(host, slot);
  }
}

/**
 * Snapshot of the currently-equipped wardrobe — { slot: pieceId }.
 */
export function getEquippedPieces(host: THREE.Object3D): Partial<Record<WardrobeSlot, string>> {
  const state = getOrCreateState(host);
  const result: Partial<Record<WardrobeSlot, string>> = {};
  state.equipped.forEach((piece, slot) => {
    result[slot] = piece.pieceId;
  });
  return result;
}

// =============================================================================
// MANIFEST FETCH (cached)
// =============================================================================

let cachedManifest: { url: string; promise: Promise<WardrobeManifest> } | null = null;

/**
 * Fetch (and memoize) the wardrobe manifest.
 */
export async function loadWardrobeManifest(baseUrl: string): Promise<WardrobeManifest> {
  const url = baseUrl.replace(/\/+$/, '') + '/wardrobe/manifest.json';
  if (cachedManifest && cachedManifest.url === url) {
    return cachedManifest.promise;
  }
  const promise = fetch(url).then((r) => {
    if (!r.ok) throw new Error(`failed to fetch wardrobe manifest: ${r.status}`);
    return r.json() as Promise<WardrobeManifest>;
  });
  cachedManifest = { url, promise };
  return promise;
}

/**
 * Reset the manifest cache (test helper / hot-reload helper).
 */
export function _clearWardrobeManifestCache(): void {
  cachedManifest = null;
}
